import type { FilterEntry } from "../components/ViewControls/types"
import type { SortCriteria } from "../types/sort"

interface VtFilterSpec {
  column: string
  operator: string
  values: string[]
}

interface VtSortSpec {
  column: string
  direction: "asc" | "desc"
}

interface BuildVtParamsOptions {
  segments: string[]
  filters: FilterEntry[]
  fromDate?: string
  toDate?: string
  dateColumn?: string
  sortBy?: SortCriteria[]
  extra?: Record<string, unknown>
}

/**
 * Convert view params (UI format) to VT transform args (backend format).
 * - Converts "not in" → "not_in"
 * - Converts fromDate/toDate (MM/DD/YYYY) to FilterSpec gte/lte (YYYY-MM-DD)
 * - Passes sort_by criteria through to backend
 */
export function buildVtParams({
  segments,
  filters,
  fromDate,
  toDate,
  dateColumn = "date",
  sortBy,
  extra,
}: BuildVtParamsOptions): { segments: string[]; filters?: VtFilterSpec[]; sort_by?: VtSortSpec[]; [key: string]: unknown } {
  const vtFilters: VtFilterSpec[] = []

  for (const [column, operator, values] of filters) {
    if (values.length === 0) continue
    vtFilters.push({
      column,
      operator: operator === "not in" ? "not_in" : operator,
      values,
    })
  }

  if (fromDate) {
    vtFilters.push({ column: dateColumn, operator: "gte", values: [toIsoDate(fromDate)] })
  }
  if (toDate) {
    vtFilters.push({ column: dateColumn, operator: "lte", values: [toIsoDate(toDate)] })
  }

  return {
    segments,
    ...(vtFilters.length > 0 && { filters: vtFilters }),
    ...(sortBy && sortBy.length > 0 && { sort_by: sortBy }),
    ...extra,
  }
}

/** Convert MM/DD/YYYY or MM/DD/YY to YYYY-MM-DD */
function toIsoDate(date: string): string {
  const [m, d, y] = date.split("/")
  const year = y.length === 2 ? `20${y}` : y
  return `${year}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`
}
